import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { fetchFinancialAssetCategory, deleteFinancialAssetCategory } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function FinancialAssetCategoryDetailPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async () => {
    try { setData(await fetchFinancialAssetCategory(categoryId)); }
    catch (e) { notify.apiError(e); }
  }, [categoryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteFinancialAssetCategory(categoryId);
      notify.success(t("message.delete.success"));
      navigate(`/strategy/${businessDomainId}/financial-asset-categories`, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  const typeNames = (data.associatedFinancialAssetTypes || [])
    .map((ft) => ft.financialAssetTypeName).join(", ");

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("financialAssetCategory.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`/strategy/${businessDomainId}/financial-asset-categories/${categoryId}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>
      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("financialAssetCategory.name")} value={data.financialAssetCategoryName} /></Grid>
          <Grid size={12}><DetailField label={t("financialAssetCategory.financialAssetTypes")} value={typeNames} /></Grid>
          <Grid size={12}><DetailField label={t("common.description")} value={data.financialAssetCategoryDescription} /></Grid>
        </Grid>
      </DetailSection>
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<AssessmentIcon />}
          onClick={() => navigate(`/strategy/${businessDomainId}/financial-asset-categories/${categoryId}/classification-criteria`)}>
          {t("financialAssetCategory.classificationCriteriaValues")}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/financial-asset-categories`)}>{t("common.back")}</Button>
      </Box>
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
    </Box>
  );
}
